import { Sliders } from "lucide-react";
import { PageHeading, Section, Field, Steps, Callout, RoleBadge, Diagram, MockRow, MockField, MockSelect, MockButton } from "../shared";

export default function AdminWorkflowRulesSection() {
  return (
    <div data-testid="guide-admin-workflow-rules">
      <PageHeading
        icon={Sliders}
        title="Workflow Rules"
        description="Per-lane (and optionally per-origin-type) settings that control which moves are allowed, when a sign-off is required, and other lane-specific behaviour."
      />

      <Callout variant="info" title="In plain words">
        These are the fine-print rules that fine-tune each lane. For example, a rule might say "items in this lane always need a sign-off" or "board-meeting items may skip review." You set a rule, choose how widely it applies, and switch it on by activating the draft.
      </Callout>

      <Section title="Who can manage it">
        <div className="flex items-center gap-2"><RoleBadge role="super_admin" /><span className="text-xs text-white/55"><span className="text-white">manage_governance</span> on a draft version.</span></div>
      </Section>

      <Section title="When do I use this?">
        <p className="text-sm text-white/65 leading-relaxed">
          Use it to tweak how a specific lane behaves without touching the rest of the system — for example to require sign-off only on one lane, or to allow review to be skipped for one kind of item.
        </p>
      </Section>

      <Section title="Adding or changing a rule">
        <Diagram
          frameLabel="Admin → Workflow Rules (on a draft version)"
          legend={[
            <>Click <span className="text-white">Add Rule</span> and pick a rule key from the catalogue.</>,
            <>Optionally narrow the <span className="text-white">scope</span> to one lane and/or origin type — leave blank for all.</>,
            <>Enter the <span className="text-white">value</span> (usually true/false or a small object).</>,
            <>Click <span className="text-white">Save</span>, then activate the draft from Versions.</>,
          ]}
        >
          <MockButton label="Add Rule" marker={1} />
          <MockRow>
            <MockSelect label="Rule key" value="requires_signoff" />
            <MockSelect label="Scope: Lane" value="SDF MD Lane" marker={2} />
          </MockRow>
          <MockField label="Value" value="true" marker={3} />
          <MockButton label="Save" marker={4} />
        </Diagram>
        <Steps steps={[
          <>On a draft governance version (clone Active first via Versions if no draft exists), open the Workflow Rules tab.</>,
          <>Click <span className="text-white">Add Rule</span>. Pick a rule key from the catalogue.</>,
          <>Optionally narrow scope by selecting a lane and/or origin type. Leave blank to apply to all.</>,
          <>Enter the value (boolean or small JSON object). The form validates the structure on save.</>,
          <>Save the row. Repeat for any other rules.</>,
          <>Activate the draft from the Versions tab when ready. The change applies only to items registered after activation.</>,
        ]} />
      </Section>

      <Section title="Rule fields">
        <div className="space-y-2">
          <Field name="Rule key" required="required"><p>Internal identifier of the rule being configured (e.g. <span className="text-white">requires_signoff</span>, <span className="text-white">allow_skip_review</span>).</p></Field>
          <Field name="Scope: Lane" required="optional"><p>Restrict the rule to one lane. Leave blank to apply to all lanes in the version.</p></Field>
          <Field name="Scope: Origin type" required="optional"><p>Restrict the rule to one origin type (e.g. only board-meeting items). Leave blank for all.</p></Field>
          <Field name="Value" required="required"><p>JSON value the rule applies — typically <span className="text-white">true</span>/<span className="text-white">false</span> or a small object. The form validates structure on save.</p></Field>
        </div>
      </Section>

      <Callout variant="info" title="Read alongside the status state machine">
        Workflow rules layer on top of the hard-coded status state machine. The state machine defines which transitions are <em>theoretically</em> possible; workflow rules narrow that down for a specific lane. See the Status Reference for the full state list.
      </Callout>
      <Callout variant="warning" title="Rules apply at registration time">
        An action item plays by the workflow rules of the governance version active when it was registered. Editing rules here does not change behaviour for already-open items.
      </Callout>
    </div>
  );
}
